import "./ForecastGraph.scss";
import React, { useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  BANK_PERFORMANCE_METRICS,
  BPM_KEY,
} from "../../../../../../utils/global";
import { useDispatch, useSelector } from "react-redux";
import {
  setGuageIsLoading,
  setLoaderFalse,
} from "../../../../../../Redux/loaderSlice";

const ForecastGraph = ({
  forecastData,
  type,
  showAPValue,
  showCohort,
  NIIValueOfAdjustedMatrics,
  graphHeight,
}) => {
  const dispatch = useDispatch();
  const selectedCohort = useSelector(
    (state) => state?.cohortReducer?.selectedCohort,
  );

  const delta_value = NIIValueOfAdjustedMatrics / 100;

  // const data = {
  //   fhsValues: forecastData?.current,
  //   forecast: forecastData?.forecasted,
  //   forecastWithAutoPilot: forecastData?.ap_used,
  //   currentCohort: forecastData?.cohort_current,
  //   forecastedCohort: forecastData?.cohort_forecasted,
  // };

  const data = {
    fhsValues: forecastData?.current,
    forecast: forecastData?.forecasted,
    forecastWithAutoPilot: forecastData?.ap_used,
    currentCohort:
      type === BPM_KEY?.financial_health_score
        ? (selectedCohort?.cohortTrend?.trend?.current ?? null)
        : forecastData?.cohort_current,
    forecastedCohort: selectedCohort?.cohortTrend?.trend?.forecasted ?? null,
  };

  const categories = [
    ...(data?.fhsValues ? data?.fhsValues?.map((item) => item.month) : []),
    ...(data?.forecast ? data?.forecast?.map((item) => item.month) : []),
  ];

  const fhsValues = data?.fhsValues?.map((item) => item.value);
  const currentCohort =
    data?.currentCohort != null
      ? data?.currentCohort?.map((item) => item.value)
      : [];

  const forecast = [
    ...new Array(data?.fhsValues?.length).fill(null),
    ...(data?.forecast ? data?.forecast?.map((item) => item.value) : []),
  ];

  let forecastWithAutoPilot = [
    ...new Array(data?.fhsValues?.length).fill(null),
    ...(data?.forecastWithAutoPilot
      ? data?.forecastWithAutoPilot?.map((item) => item.value)
      : []),
  ];

  forecastWithAutoPilot = forecastWithAutoPilot.map((item) => {
    if (item === null || item === undefined) return item;

    const adjusted = (item * delta_value).toFixed(2);
    return parseFloat((parseFloat(adjusted) + item).toFixed(2));
  });

  const forecastedCohort = [
    ...new Array(data?.fhsValues?.length).fill(null),
    ...(data?.forecastedCohort
      ? data?.forecastedCohort?.map((item) => item.value)
      : []),
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setGuageIsLoading(false));
      dispatch(setLoaderFalse());
    }, 1000);

    return () => clearTimeout(timer);
  }, [selectedCohort, dispatch]);

  const options = useMemo(
    () => ({
      chart: {
        type: "line",
        height: graphHeight,
        backgroundColor: "transparent",
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories,
        crosshair: {
          width: 1,
          color: "#00D2A3",
          dashStyle: "Dash",
          zIndex: 1,
        },
        gridLineDashStyle: "dash",
        lineWidth: 0,
      },
      yAxis: {
        title: {
          text: null,
        },
        gridLineDashStyle: "dash",
        labels: {
          formatter: function () {
            const value = this.value * 1000;
            let formattedValue;

            if (Math.abs(value) >= 1e12) {
              formattedValue = (value / 1e12).toFixed(2) + "T";
            } else if (Math.abs(value) >= 1e9) {
              formattedValue = (value / 1e9).toFixed(2) + "B";
            } else if (Math.abs(value) >= 1e6) {
              formattedValue = (value / 1e6).toFixed(2) + "M";
            } else if (Math.abs(value) >= 1e3) {
              formattedValue = (value / 1e3).toFixed(2) + "K";
            } else {
              formattedValue = value;
            }

            return `${BPM_KEY?.net_interest_income !== type ? this.value : "$" + formattedValue}`;
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          const formatValue = (value) => {
            if (Math.abs(value) >= 1e12) {
              return (value / 1e12).toFixed(2) + "T";
            } else if (Math.abs(value) >= 1e9) {
              return (value / 1e9).toFixed(2) + "B";
            } else if (Math.abs(value) >= 1e6) {
              return (value / 1e6).toFixed(2) + "M";
            } else if (Math.abs(value) >= 1e3) {
              return (value / 1e3).toFixed(2) + "K";
            } else {
              return value;
            }
          };

          return this.points
            ?.sort((a, b) => b.y - a.y)
            ?.map(
              (point) =>
                `</br><span style="color:${point.color}">\u25CF</span> ${point.series.name
                }: ${BPM_KEY?.net_interest_income !== type
                  ? point.y
                  : "$" + formatValue(point.y * 1000)
                }
                `,
            )
            ?.join("");
        },
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false,
          },
          lineWidth: 2.5,
        },
      },
      series: [
        {
          name:
            BANK_PERFORMANCE_METRICS?.find((i) => i.key === type)?.title +
            " Value",
          data: fhsValues,
          color: "#58B2E8",
        },
        {
          name: "Cohort",
          data: currentCohort,
          color: "#56d8c0",
        },
        {
          name: "Forecast",
          data: forecast,
          color: "#58B2E8",
          dashStyle: "ShortDash",
        },

        ...(showAPValue && type === BPM_KEY?.net_interest_income
          ? [
            {
              name: "Forecast With Autopilot",
              data: forecastWithAutoPilot,
              color: "#6272D0",
              dashStyle: "ShortDash",
            },
          ]
          : []),
        ...(showCohort
          ? [
            {
              name: "Cohort Forecast",
              data: forecastedCohort,
              color: "#56d8c0",
              dashStyle: "ShortDash",
            },
          ]
          : []),
      ],
      legend: {
        enabled: false,
      },
    }),
    [
      categories,
      fhsValues,
      forecast,
      forecastWithAutoPilot,
      forecastedCohort,
      type,
      window.innerWidth,
    ],
  );

  return (
    <div className="forecast-graph">
      <div className="custom-legend mb-5 ms-3">
        <div className="legend-item">
          <span className="line thick-line"></span>
          <span>
            {BANK_PERFORMANCE_METRICS?.find((i) => i.key === type)?.title} Value
          </span>
        </div>
        <div className="legend-item">
          <span className="line dashed-line"></span>
          <span>Forecast</span>
        </div>
        {showCohort && (
          <div className="legend-item">
            <span className="line thick-line cohort-color"></span>
            <span>Cohort</span>
          </div>
        )}
        {showCohort && (
          <div className="legend-item">
            <span className="line dashed-line cohort-color"></span>
            <span>Cohort Forecast</span>
          </div>
        )}
        <div
          className={`legend-item ${showAPValue && type === BPM_KEY?.net_interest_income ? "" : "d-none"}`}
        >
          <span className="line dashed-line-ap"></span>
          <span>Forecast With Autopilot</span>
        </div>
      </div>
      {fhsValues && (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </div>
  );
};

export default ForecastGraph;
